import React, { useEffect, useRef, useState } from 'react';

import './style.scss'
import { SEOHeader } from 'views/components';
import { InputField, SelectField } from 'views/components/form-inputs';
import CustomToast from 'views/components/form-inputs/alert';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { InputTextarea } from 'primereact/inputtextarea';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { showTostify } from 'utils/interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from 'utils/routes';
import {  notificationService } from 'services';
import { parseISO } from 'date-fns';
import moment from 'moment';
import { storeService } from 'services/store.service';

const initialValue = {
  title: "",
  description: "",
  start_date: "",
  end_date: "",
  store_id: "",
};

const AddNotification = () => {
  const toastRef = useRef<any>(null);
  const navigation = useNavigate()
  const params = useParams()
  const id: any = params.id
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [initialData, setInitialData] = useState(initialValue);

  const formik: any = useFormik({
    initialValues: initialData,
    enableReinitialize: true,
    validationSchema: Yup.object({
      title: Yup.string().required('Title is required'),
      description: Yup.string().required('Description is required'),
      start_date: Yup.string().required('Start date is required'),
      store_id: Yup.string().required('Store ID is required'),
      end_date: Yup.string().required('End date is required'),
    }),
    onSubmit: async (values: any) => {
      setLoading(true);
      try {
        if (id) {
          delete values.id
          delete values.created_by
          delete values.created_at
          delete values.updated_at
          const data = await notificationService.updateNotification(id, values);
          if (data.error === false) {
            showTostify(toastRef, 'success', data.message);
            setTimeout(() => {
              navigation(`${routes.notification}`);
            }, 800)
          }
          else {
            showTostify(toastRef, 'error', data.message);
          }
        }
        else {
          const data = await notificationService.createNotification(values);
          if (data.error === false) {
            showTostify(toastRef, 'success', data.message);
            setTimeout(() => {
              navigation(`${routes.notification}`);
            }, 800)
          }
          else {
            showTostify(toastRef, 'error', data.message);
          }
        }
      } catch (err: any) {
        showTostify(toastRef, 'error', err.message);
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (id) {
      setLoading(true)
      const getNotificationDetails = async () => {
        const data = await notificationService.getNotificationDetails(id);

        if (!data.error) {
          setInitialData(data)
          setLoading(false);
        } else {
          showTostify(toastRef, 'error', data.message);
          setLoading(false);
        }
      };
      getNotificationDetails();
    }
  }, [id]);

  useEffect(() => {
    const fetchStores = async () => {
      const response = await storeService.getStoresReport();
      if (!response.error) {
        setStores(response.items || []);
      } else {
        showTostify(toastRef, 'error', response.message);
      }
    };

    fetchStores();
  }, []);
  
  return (
    <>
      <CustomToast ref={toastRef} />
      <SEOHeader title="Create Notifications" />

      <div className="quicklinks-page">
        <div className="container">
          <div className='bg-white border-round-2xl w-75'>
            <form onSubmit={formik.handleSubmit} >
              <div className="p-4 border-bottom-1 border-primary-color">
                <h2 className='text-start m-0'>{id ? "Edit Notification" : "Add New Notification"}</h2>
              </div>
              <div className="grid m-0 py-4 p-3">
                <div className="grid m-0">
                  <div className="col-12 md:col-6 lg:col-6">
                    <InputField
                      name="title"
                      label="Title"
                      required={true}
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik?.touched.title && formik.errors.title}
                    />
                  </div>

                  <div className="col-12 md:col-6 lg:col-6">
                    <SelectField
                      name="store_id"
                      label="Store"
                      filter
                      showClear
                      value={formik.values.store_id || ""}
                      onChange={(e) => formik.setFieldValue('store_id', e.value)}
                      options={stores.map((store: any) => ({ label: store.title, value: store.id }))}
                      error={formik.touched.store_id && formik.errors.store_id}
                      required={true}
                      className="my-custom-class"
                    />
                  </div>

                  <div className="col-12 md:col-6 lg:col-6">
                    <div className="form-group mb-2">
                      <label htmlFor="start_date">Start Date<span className='required-field'>*</span></label>
                      <Calendar
                        id="start_date"
                        name="start_date"
                        value={formik.values.start_date ? parseISO(formik.values.start_date) : null}
                        onChange={(e) => {
                          if (e.value) {
                            const formattedDate = moment(e.value).toISOString();
                            formik.setFieldValue('start_date', formattedDate);
                          } else {
                            formik.setFieldValue('start_date', null);
                          }
                        }}
                        showIcon
                        minDate={new Date()}
                        placeholder="Enter here"
                        className={formik.touched?.start_date && formik.errors?.start_date ? 'p-invalid' : ''}
                      />
                      {formik.touched?.start_date && formik.errors?.start_date && (
                        <Message severity="error" text={formik.errors.start_date} />
                      )}
                    </div>
                  </div>

                  <div className="col-12 md:col-6 lg:col-6">
                    <div className="form-group mb-2">
                      <label htmlFor="end_date">End Date<span className='required-field'>*</span></label>
                      <Calendar
                        id="end_date"
                        name="end_date"
                        value={formik.values?.end_date ? parseISO(formik.values.end_date) : null}
                        onChange={(e) => {
                          if (e.value) {
                            const formattedDate = moment(e.value).toISOString();
                            formik.setFieldValue('end_date', formattedDate);
                          } else {
                            formik.setFieldValue('end_date', null);
                          }
                        }}
                        minDate={new Date()}
                        showIcon
                        placeholder="Enter here"
                        className={formik.touched?.end_date && formik.errors?.end_date ? 'p-invalid' : ''}
                      />
                      {formik.touched?.end_date && formik.errors?.end_date && (
                        <Message severity="error" text={formik.errors.end_date} />
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group mb-2">
                      <label htmlFor="description">Description <span className="required-field">*</span></label>
                      <InputTextarea
                        placeholder="Enter here"
                        rows={3}
                        className='w-full'
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik?.touched.description && formik.errors.description && <Message severity="error" text={formik.errors.description} />}
                    </div>
                  </div>

                  <div className="col-12 text-center">
                    <Button
                      label={id ? "Edit Notification" : "Add Notification"}
                      type="submit"
                      className="primary"
                      loading={loading}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddNotification;

